import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './EmailVerify.module.css';

const ResetPassword = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});

  const validatePassword = (password: string): boolean => {
    if (password.length < 6) {
      setErrors({ password: t('login.passwordTooShort', 'Password must be at least 6 characters') });
      return false;
    }
    setErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords
    if (!validatePassword(newPassword)) {
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrors({ confirm: t('login.passwordsDoNotMatch', 'Passwords do not match') });
      return;
    }

    if (!token) {
      setStatus('error');
      setMessage(t('login.invalidResetToken', 'Invalid reset token'));
      return;
    }

    setStatus('loading');
    setErrors({});

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/auth/reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            newPassword,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(t('login.passwordResetSuccess', 'Password reset successfully! Redirecting to login...'));
        
        // Redirect after 3 seconds
        setTimeout(() => {
          navigate('/login-page');
        }, 3000);
      } else {
        setStatus('error');
        setMessage(data.message || t('login.passwordResetFailed', 'Password reset failed. The link may be expired or invalid.'));
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setStatus('error');
      setMessage(t('login.passwordResetFailed', 'Password reset failed. The link may be expired or invalid.'));
    }
  };

  return (
    <div className={styles.verifyContainer}>
      <div className={styles.verifyCard}>
        {/* Icon */}
        <div className={`${styles.iconContainer} ${status === 'success' ? styles.success : status === 'error' ? styles.error : ''}`}>
          {status === 'loading' && (
            <div className={styles.spinner}></div>
          )}
          {status === 'success' && (
            <span className={`${styles.icon} ${styles.checkmark}`}>✓</span>
          )}
          {status === 'error' && (
            <span className={styles.icon}>✕</span>
          )}
          {status === 'idle' && (
            <span className={styles.icon}>🔑</span>
          )}
        </div>

        {/* Content */}
        <div className={styles.content}>
          <h1 className={styles.title}>
            {status === 'success' 
              ? t('login.passwordReset', 'Password Reset!')
              : t('login.resetYourPassword', 'Reset Your Password')
            }
          </h1>

          {status === 'success' ? (
            <>
              <p className={styles.message}>{message}</p>
              <div className={styles.buttonGroup}>
                <Link to="/login-page" className={`${styles.button} ${styles.primaryButton}`}>
                  {t('login.goToLogin', 'Go to Login')}
                </Link>
              </div>
            </>
          ) : status === 'error' ? (
            <>
              <p className={styles.message}>{message}</p>
              <div className={styles.buttonGroup}>
                <Link to="/login-page" className={`${styles.button} ${styles.secondaryButton}`}>
                  {t('login.backToLogin', 'Back to Login')}
                </Link>
                <Link to="/forgot-password" className={`${styles.button} ${styles.primaryButton}`}>
                  {t('login.requestNewLink', 'Request New Link')}
                </Link>
              </div>
            </>
          ) : (
            <>
              <p className={styles.message}>
                {t('login.enterNewPassword', 'Enter your new password below.')}
              </p>

              <form onSubmit={handleSubmit}>
                <div className={styles.inputGroup}>
                  <label htmlFor="newPassword" className={styles.inputLabel}>
                    {t('login.newPassword', 'New Password')}
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={styles.inputField}
                    placeholder={t('login.enterNewPassword', 'Enter new password')}
                    disabled={status === 'loading'}
                    required
                    minLength={6}
                  />
                  {errors.password && (
                    <div className={styles.errorText}>
                      ⚠️ {errors.password}
                    </div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="confirmPassword" className={styles.inputLabel}>
                    {t('login.confirmNewPassword', 'Confirm New Password')}
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={styles.inputField}
                    placeholder={t('login.confirmPassword', 'Confirm password')}
                    disabled={status === 'loading'}
                    required
                    minLength={6}
                  />
                  {errors.confirm && (
                    <div className={styles.errorText}>
                      ⚠️ {errors.confirm}
                    </div>
                  )}
                </div>

                <div className={styles.buttonGroup}>
                  <Link 
                    to="/login-page" 
                    className={`${styles.button} ${styles.secondaryButton}`}
                  >
                    {t('login.cancel', 'Cancel')}
                  </Link>
                  <button
                    type="submit"
                    className={`${styles.button} ${styles.primaryButton}`}
                    disabled={status === 'loading'}
                  >
                    {status === 'loading' 
                      ? t('login.resetting', 'Resetting...')
                      : t('login.resetPassword', 'Reset Password')
                    }
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
